import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { PopupData, Service, UserListOption } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getInitials } from 'webapp/utils/common';

import ClientNewComponent from 'webapp/components/client/new';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import type Space from 'webapp/models-custom/space';

export default class AccountClientPopupCreateComponent extends PopupInnerModalComponent<PopupData.Account.Client.Create> {
	@service('popup') declare popupService: Service.Popup;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.NEW_CLIENT_FORM, {
			origin: this.popupData.origin,
		});
	}

	get model() {
		return this.popupData.model;
	}

	get usersForAccountManager() {
		return (
			this.mmUsersService.activatedUserRecords
				// non-admin users can't select different owner (they don't have rights to change app objects permissions)
				.filter((user) => {
					return (
						this.currentUserService.isAdmin ||
						this.currentUserService.numericId === +user.id!
					);
				})
				.map((user) => {
					const userId = user.id!,
						userData: UserListOption = {
							value: userId,
							label: user.name,
						};

					if (user.avatar) {
						userData.avatar = user.avatar;
					} else if (user.name) {
						userData.initials = getInitials(user.name);
					}

					const userModel = this.mmUsersService.findModel(userId);
					userData.disabled = !!userModel?.isViewer;

					return userData;
				})
		);
	}

	@action
	invite(forClient?: Space) {
		if (this.currentUserService.isVerified) {
			if (this.billingService.checkIfCan(BillingFeature.USERS, true)) {
				this.mmUsersService.openUserInvitePopup({
					maximumNumberOfNewUsers: 1,
					onUserInvited: (userRecords) => {
						const invitedUser = userRecords[0];
						if (invitedUser) {
							const user = this.mmUsersService.createNewModel(invitedUser);
							if (forClient && user.numericId) {
								forClient.ownerId = user.numericId;
							}
						}
					},
				});
			}
		} else {
			this.currentUserService.openEmailVerificationInfoPopup('invite new users');
		}
	}

	override willDestroy(): void {
		this.model.rollback();
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="UserNew" @size="lg" />
			Add account
		</div>
		<div class="content">
			<ClientNewComponent
				@model={{this.model}}
				@usersForAccountManager={{this.usersForAccountManager}}
				@onInvite={{this.invite}}
				@onContentChanged={{this.reposition}}
				@onClose={{this.close}}
			/>
		</div>
	</template>
}
